'use client';

import NextLink from 'next/link';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Image from '@alltrails/core/components/Image';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import DynamicButton from '@alltrails/denali/components/DynamicButton';
import IconRenderer from '@alltrails/denali/components/IconRenderer';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import ArrowSq from '@alltrails/denali/icons/ArrowSq';
import ImageSlash from '@alltrails/denali/icons/ImageSlash';
import { Size } from '@alltrails/denali/types';
import { usePOIProfilePhoto } from '@alltrails/explore-service/hooks/usePOIProfilePhoto';
import type { POISearchResult } from '@alltrails/types/AlgoliaResults';
import getPOIIcon from '@alltrails/search/utils/getPOIIcon';
import sharedStyles from '../AreaSidebar/AreaDetailsLink.module.scss';

type POIDetailsLinkProps = {
  poi: POISearchResult;
  variant?: Size<'sm' | 'lg'>;
};

const POIDetailsLink = ({ poi, variant = 'lg' }: POIDetailsLinkProps) => {
  const isMobile = useIsMobileSizedScreen();
  const languageRegionCode = useLanguageRegionCode();
  const { ID, photos_count, name, slug } = poi;
  const profileImageUrl = usePOIProfilePhoto({ poiId: ID, width: 108, height: 108 });
  const href = wrapUrlSafe(`/${slug}`, languageRegionCode);

  const [imageError, setImageError] = useState(false);
  const iconComponent = getPOIIcon(poi.subtype, true /* filled */);

  if (variant === 'sm') {
    return (
      <DynamicButton
        iconFallback={{ Component: iconComponent }}
        imageAlt={poi.name}
        linkInfo={{ href, target: '_blank' }}
        src={profileImageUrl || undefined}
        testId="poi-details-link-sm"
        text={<FormattedMessage defaultMessage="Explore this spot" />}
      />
    );
  }

  return (
    <NextLink aria-labelledby="poi-details-link-text" className={sharedStyles.container} href={href} prefetch={false} target="_blank">
      {!!photos_count && !imageError ? (
        <Image alt={name} className={sharedStyles.profileImage} height={56} onError={() => setImageError(true)} src={profileImageUrl} width={56} />
      ) : (
        <div className={sharedStyles.fallbackIcon}>
          <IconRenderer icon={{ Component: imageError ? ImageSlash : iconComponent, color: '--color-neutral-white' }} defaults={{}} />
        </div>
      )}
      <div className={sharedStyles.name} id="poi-details-link-text">
        {isMobile ? (
          <FormattedMessage defaultMessage="Explore {poiName}" values={{ poiName: name }} />
        ) : (
          <FormattedMessage defaultMessage="Explore this spot" />
        )}
      </div>
      <ArrowSq color="--color-text-secondary" orientation="right" size="sm" />
    </NextLink>
  );
};

export default POIDetailsLink;
